import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";

/**
 * 내 채팅방들의 미읽음 메시지 수를 관리하는 훅.
 *
 * 설계 원칙:
 * - myRoomIdsRef + 수동 증감 방식(race condition 발생) 대신
 *   Realtime INSERT 시 DB 재조회(fetchUnread)로 항상 정확한 값을 유지
 * - markRoomAsRead : 채팅방 입장 시 즉시 UI 업데이트 (DB 쿼리 없이)
 */
export function useUnreadMessages(myTeamName) {
  const [unreadByRoom, setUnreadByRoom] = useState({});

  // ────────────────────────────────────────
  // DB에서 방별 미읽음 수 전체 재조회
  // ────────────────────────────────────────
  const fetchUnread = useCallback(async () => {
    if (!myTeamName) { setUnreadByRoom({}); return; }

    // 내가 속한 채팅방 ID 목록
    const [r1, r2] = await Promise.all([
      supabase.from("chat_rooms").select("id").eq("team1_name", myTeamName),
      supabase.from("chat_rooms").select("id").eq("team2_name", myTeamName),
    ]);
    const roomIds = [...new Set([
      ...(r1.data || []).map((r) => r.id),
      ...(r2.data || []).map((r) => r.id),
    ])];

    if (!roomIds.length) { setUnreadByRoom({}); return; }

    // 내가 받은 메시지 중 is_read = false 인 것만 room_id 별 카운트
    const { data: msgs, error } = await supabase
      .from("messages")
      .select("room_id")
      .in("room_id", roomIds)
      .neq("sender_name", myTeamName)
      .eq("is_read", false);

    if (error) {
      // is_read 컬럼이 없으면 여기서 에러 — SQL 실행 필요
      return;
    }

    const counts = {};
    (msgs || []).forEach((m) => {
      counts[m.room_id] = (counts[m.room_id] || 0) + 1;
    });
    setUnreadByRoom(counts);
  }, [myTeamName]);

  // 마운트 / myTeamName 변경 시 초기 조회
  useEffect(() => { fetchUnread(); }, [fetchUnread]);

  // ────────────────────────────────────────
  // Realtime 구독
  // INSERT : 상대방 메시지가 새로 들어오면 재조회
  // ────────────────────────────────────────
  useEffect(() => {
    if (!myTeamName) return;

    const channel = supabase
      .channel(`unread-watch-${myTeamName}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "messages",
      }, (payload) => {
        if (!payload.new) return;
        // 내가 보낸 메시지는 스킵
        if (payload.new.sender_name === myTeamName) return;
        // 상대방 메시지 → DB 재조회로 정확한 카운트 반영
        fetchUnread();
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [myTeamName, fetchUnread]);

  // ────────────────────────────────────────
  // 채팅방 입장 시 즉시 UI 업데이트 (DB 쿼리 없이)
  // ────────────────────────────────────────
  const markRoomAsRead = useCallback((roomId) => {
    setUnreadByRoom((prev) => ({ ...prev, [roomId]: 0 }));
  }, []);

  const totalUnread = Object.values(unreadByRoom).reduce((a, b) => a + b, 0);

  return { unreadByRoom, totalUnread, markRoomAsRead };
}
